import { useEffect, useState } from 'react';
import { updateUrgencySignalsPageProperties } from '@/utils/dataLayer';
import { useDisplayStatus } from '@/components/_shared/contexts/DisplayStatusContext';
import { HighLevelStatus } from '@/utils/highLevelStatus';
import type { Signal } from './signal';
import { getVisibleBadges } from './utils';
import { UrgencySignalBadge } from './UrgencySignalBadge';

const MAX_VISIBLE_SIGNALS = 1;

const URGENCY_SIGNALS_SUPPRESSED_STATUSES = new Set<HighLevelStatus>([
  HighLevelStatus.SOLD_OUT,
  HighLevelStatus.SALES_ENDED,
  HighLevelStatus.CANCELLED,
  HighLevelStatus.EVENT_ENDED,
  HighLevelStatus.UNAVAILABLE,
]);

interface Props {
  eventId: string;
}

export const UrgencySignals: React.FC<Props> = ({ eventId }) => {
  const { displayStatus, isDisplayStatusStable } = useDisplayStatus();

  const suppressUrgencySignals = URGENCY_SIGNALS_SUPPRESSED_STATUSES.has(displayStatus);
  const urgencySignals = useUrgencySignals(eventId, suppressUrgencySignals, isDisplayStatusStable);

  if (!isDisplayStatusStable) {
    return null;
  }

  if (suppressUrgencySignals) {
    return null;
  }

  const visibleBadges = getVisibleBadges(urgencySignals.slice(0, MAX_VISIBLE_SIGNALS));
  return visibleBadges.map((badge) => <UrgencySignalBadge key={badge.signal} badge={badge} />);
};

function useUrgencySignals(eventId: string, suppressUrgencySignals: boolean, isDisplayStatusStable: boolean) {
  const [urgencySignals, setUrgencySignals] = useState<Signal[]>([]);

  useEffect(() => {
    if (!isDisplayStatusStable || suppressUrgencySignals) {
      setUrgencySignals([]);
      updateUrgencySignalsPageProperties([]);
      return;
    }

    fetch(`/e/api/${eventId}/urgency-signals`)
      .then(async (res) => {
        if (!res.ok) {
          setUrgencySignals([]);
          updateUrgencySignalsPageProperties([]);
          return;
        }
        const newSignals: string[] = await res.json();
        setUrgencySignals(newSignals as Signal[]);
        updateUrgencySignalsPageProperties(newSignals);
      })
      .catch(() => {
        setUrgencySignals([]);
        updateUrgencySignalsPageProperties([]);
      });
  }, [eventId, suppressUrgencySignals, isDisplayStatusStable]);

  return urgencySignals;
}
